// API client for REI Offer Analysis backend
// Maps exactly to the API endpoints defined in BACKEND_ENGINEERING_PROMPT.md

const API_BASE = (import.meta as unknown as { env: Record<string, string> }).env.VITE_API_URL || '/api';

interface Contact {
    id: string;
    name: string;
    email: string;
    company?: string;
}

interface Report {
    id: string;
    jobId?: string;
    contactId: string;
    locationId?: string;
    propertyType: 'single' | 'multi';
    sections: Record<string, unknown>;
    crmFields: Array<{ key: string; label: string; val: string }>;
    propertyName?: string;
    propertyAddress?: string;
    pdfUrl?: string;
    downloadUrl?: string;
    legacySource?: string;
    archived?: boolean;
    createdAt: string;
}

type AnalysisTrace = Record<string, unknown>;

interface LOI {
    id: string;
    contactId: string;
    reportId: string;
    templateId: string;
    tier: string;
    status: string;
    downloadUrl?: string;
    address?: string;
    offerPrice?: string;
    template?: string;
    content?: string;
    htmlContent?: string;
    createdAt: string;
}

interface RawExtractionSnapshot {
    fields: Record<string, string>;
    extractedText: string;
    imageCount: number;
    durationMs: number;
}

interface AnalysisStartBody {
    contactId?: string;
    propertyType: string;
    fields: Record<string, string>;
    fileIds: string[];
    standby?: boolean;
    rawExtractedFields?: Record<string, string>;
    rawExtractedText?: string;
    rawExtractedImageCount?: number;
    rawExtractionDurationMs?: number;
}

async function apiFetch<T>(path: string, options?: RequestInit): Promise<T> {
    const params = new URLSearchParams(window.location.search);
    const locationId = params.get('locationId') || '';
    const token = localStorage.getItem('rei_token') || '';

    const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        ...(locationId ? { 'X-Location-Id': locationId } : {}),
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        ...(options?.headers as Record<string, string> ?? {}),
    };

    const res = await fetch(`${API_BASE}${path}`, {
        ...options,
        headers,
    });

    if (!res.ok) {
        let errorPayload: { error?: string } = { error: `HTTP ${res.status}` };
        try { errorPayload = await res.json() as { error?: string }; } catch { /* ignore */ }
        throw new Error(errorPayload.error ?? `HTTP ${res.status}`);
    }

    return res.json() as Promise<T>;
}

export const api = {
    // ── Contact ──────────────────────────────────────────────────────────
    getContact: (contactId: string) =>
        apiFetch<{ contact: Contact }>(`/contacts/${contactId}`),

    saveCRMFields: (contactId: string, fields: Array<{ key: string; value: string }>) =>
        apiFetch<{ success: boolean }>(`/contacts/${contactId}/fields`, {
            method: 'POST',
            body: JSON.stringify({ fields }),
        }),

    // ── Upload ───────────────────────────────────────────────────────────
    uploadFiles: async (files: File[]): Promise<{ files: Array<{ id: string; name: string; size: number; status: string }> }> => {
        const params = new URLSearchParams(window.location.search);
        const locationId = params.get('locationId') || '';
        const form = new FormData();
        files.forEach(f => form.append('files', f));
        const res = await fetch(`${API_BASE}/uploads`, {
            method: 'POST',
            headers: { 'X-Location-Id': locationId },
            body: form,
        });
        if (!res.ok) { const e = await res.json() as { error?: string }; throw new Error(e.error ?? 'Upload failed'); }
        return res.json() as Promise<{ files: Array<{ id: string; name: string; size: number; status: string }> }>;
    },

    // ── Image Upload (for template editor) ─────────────────────────────
    uploadImage: async (file: File): Promise<{ url: string; id: string }> => {
        const params = new URLSearchParams(window.location.search);
        const locationId = params.get('locationId') || '';
        const form = new FormData();
        form.append('image', file);
        const res = await fetch(`${API_BASE}/uploads/image`, {
            method: 'POST',
            headers: { 'X-Location-Id': locationId },
            body: form,
        });
        if (!res.ok) { const e = await res.json() as { error?: string }; throw new Error(e.error ?? 'Image upload failed'); }
        return res.json() as Promise<{ url: string; id: string }>;
    },

    // ── Extract ──────────────────────────────────────────────────────────
    extractFields: (body: { fileIds: string[]; propertyType: string }) =>
        apiFetch<RawExtractionSnapshot>('/extract', {
            method: 'POST',
            body: JSON.stringify(body),
        }),

    // ── Analysis ─────────────────────────────────────────────────────────
    startAnalysis: (body: AnalysisStartBody) =>
        apiFetch<{ jobId: string }>('/analysis/start', {
            method: 'POST',
            body: JSON.stringify(body),
        }),

    pollAnalysis: (jobId: string) =>
        apiFetch<{ status: string; progress: number; stage: string; reportId?: string; error?: string }>(`/analysis/${jobId}`),

    // ── Reports ──────────────────────────────────────────────────────────
    getReport: (reportId: string) =>
        apiFetch<{ report: Report }>(`/reports/${reportId}`),

    getAnalysisTrace: (reportId: string) =>
        apiFetch<{ trace: AnalysisTrace }>(`/reports/${reportId}/trace`),

    getReports: (contactId: string) =>
        apiFetch<{ reports: Report[] }>(`/reports?contactId=${contactId}`),

    saveReportToDocs: (reportId: string) =>
        apiFetch<{ documentId: string; success?: boolean; warning?: string }>(`/reports/${reportId}/save`, {
            method: 'POST',
            body: JSON.stringify({}),
        }),

    downloadReport: async (reportId: string): Promise<Blob> => {
        const params = new URLSearchParams(window.location.search);
        const locationId = params.get('locationId') || '';
        const res = await fetch(`${API_BASE}/reports/${reportId}/download`, {
            headers: { 'Content-Type': 'application/json', 'X-Location-Id': locationId },
        });
        if (!res.ok) { const e = await res.json() as { error?: string }; throw new Error(e.error ?? 'Download failed'); }
        return res.blob();
    },

    deleteReport: (reportId: string) =>
        apiFetch<{ success: boolean }>(`/reports/${reportId}`, { method: 'DELETE' }),

    archiveReport: (reportId: string, archived: boolean) =>
        apiFetch<{ success: boolean }>(`/reports/${reportId}/archive`, {
            method: 'PATCH',
            body: JSON.stringify({ archived }),
        }),

    updateReportSection: (reportId: string, sectionKey: string, sectionData: Record<string, unknown>) =>
        apiFetch<{ success: boolean }>(`/reports/${reportId}/sections/${sectionKey}`, {
            method: 'PUT',
            body: JSON.stringify(sectionData),
        }),

    // ── LOI Templates ────────────────────────────────────────────────────
    getTemplates: () =>
        apiFetch<{ templates: LOITemplate[] }>('/loi/templates'),

    getTemplate: (id: string) =>
        apiFetch<LOITemplate>(`/loi/templates/${id}`),

    createTemplate: (body: { name: string; propertyType: string; htmlContent: string; emailJson?: unknown }) =>
        apiFetch<LOITemplate>('/loi/templates', { method: 'POST', body: JSON.stringify(body) }),

    updateTemplate: (id: string, body: { name?: string; htmlContent?: string; emailJson?: unknown }) =>
        apiFetch<LOITemplate>(`/loi/templates/${id}`, { method: 'PUT', body: JSON.stringify(body) }),

    deleteTemplate: (id: string) =>
        apiFetch<{ success: boolean }>(`/loi/templates/${id}`, { method: 'DELETE' }),

    previewTemplate: async (id: string): Promise<Blob> => {
        const params = new URLSearchParams(window.location.search);
        const locationId = params.get('locationId') || '';
        const res = await fetch(`${API_BASE}/loi/templates/${id}/preview`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-Location-Id': locationId },
        });
        if (!res.ok) { const e = await res.json() as { error?: string }; throw new Error(e.error ?? 'Preview failed'); }
        return res.blob();
    },

    // ── LOI ──────────────────────────────────────────────────────────────
    generateLOI: (body: {
        contactId: string;
        reportId: string;
        templateId: string;
        tier: string;
        mode?: 'ai' | 'template';
        options: {
            contingencies: boolean; earnest: boolean; timeline: boolean;
            contingenciesText?: string; earnestText?: string; timelineText?: string;
        };
    }) =>
        apiFetch<{ loiId: string; downloadUrl: string }>('/loi/generate', {
            method: 'POST',
            body: JSON.stringify(body),
        }),

    saveLOI: (loiId: string) =>
        apiFetch<{ documentId: string; success?: boolean; warning?: string }>(`/loi/${loiId}/save`, { method: 'POST' }),

    getLOI: (loiId: string) =>
        apiFetch<LOI>(`/loi/${loiId}`),

    updateLOIContent: (loiId: string, body: { content: string; htmlContent?: string }) =>
        apiFetch<{ success: boolean; downloadUrl: string }>(`/loi/${loiId}/content`, {
            method: 'PATCH',
            body: JSON.stringify(body),
        }),

    getLOIs: (contactId: string) =>
        apiFetch<{ lois: LOI[] }>(`/lois?contactId=${contactId}`),

    // ── Email ────────────────────────────────────────────────────────────
    sendEmail: (body: { contactId: string; loiId: string; subject?: string }) =>
        apiFetch<{ success: boolean }>('/email/send', {
            method: 'POST',
            body: JSON.stringify(body),
        }),

    // ── Custom Fields ────────────────────────────────────────────────────
    getCustomFields: () =>
        apiFetch<{ customFields: GHLCustomField[] }>('/custom-fields'),

    createCustomField: (body: { name: string; dataType: string; placeholder?: string }) =>
        apiFetch<{ customField: GHLCustomField }>('/custom-fields', {
            method: 'POST',
            body: JSON.stringify(body),
        }),

    getFieldMappings: () =>
        apiFetch<{ mappings: Array<{ crmKey: string; ghlFieldId: string; ghlFieldKey: string; ghlFieldName: string }> }>('/custom-fields/mappings'),

    saveFieldMappings: (mappings: Array<{ crmKey: string; ghlFieldId: string; ghlFieldKey?: string; ghlFieldName?: string }>) =>
        apiFetch<{ success: boolean }>('/custom-fields/mappings', {
            method: 'PUT',
            body: JSON.stringify({ mappings }),
        }),

    saveCustomFieldValues: (contactId: string, fieldValues: Array<{ id: string; key?: string; field_value: string }>) =>
        apiFetch<{ success: boolean }>(`/contacts/${contactId}/custom-fields`, {
            method: 'PUT',
            body: JSON.stringify({ customFields: fieldValues }),
        }),

    // ── Auth / SSO ─────────────────────────────────────────────────────
    decryptSSO: (ssoKey: string) =>
        apiFetch<{ locationId?: string; contactId?: string; userId?: string; companyId?: string; userName?: string; email?: string }>('/auth/decrypt-sso', {
            method: 'POST',
            body: JSON.stringify({ ssoKey }),
        }),

    getAuthStatus: (locationId: string, returnTo?: string) => {
        const qs = new URLSearchParams({ locationId });
        if (returnTo) qs.set('returnTo', returnTo);
        return apiFetch<{ connected: boolean; expired?: boolean; installUrl?: string }>(`/auth/status?${qs.toString()}`);
    },

    refreshToken: (locationId: string) =>
        apiFetch<{ success: boolean }>('/auth/refresh', {
            method: 'POST',
            body: JSON.stringify({ locationId }),
        }),

    // ── Investor Settings ─────────────────────────────────────────────
    getSettings: () =>
        apiFetch<{ settings: InvestorSettings }>('/settings'),

    updateSettings: (settings: Partial<InvestorSettings>) =>
        apiFetch<{ settings: InvestorSettings; success: boolean }>('/settings', {
            method: 'PUT',
            body: JSON.stringify(settings),
        }),
};

// ═══════════════════════════════════════════════════════════════════════════
// Standalone Auth + Contacts API
// ═══════════════════════════════════════════════════════════════════════════

interface StandaloneUser {
    id: string;
    email: string;
    name: string;
    company: string;
    plan: 'demo' | 'pro';
    emailVerified?: boolean;
}

interface AppContact {
    _id: string;
    userId: string;
    name: string;
    email: string;
    phone: string;
    company: string;
    address: string;
    notes: string;
    source: 'manual' | 'extracted' | 'csv';
    createdAt: string;
}

// Backward compat alias
type StandaloneContact = AppContact;

function getStandaloneToken(): string {
    return localStorage.getItem('rei_token') || '';
}

function standaloneHeaders(): Record<string, string> {
    return {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getStandaloneToken()}`,
    };
}

async function standaloneFetch<T>(path: string, options?: RequestInit): Promise<T> {
    const res = await fetch(`${API_BASE}${path}`, {
        ...options,
        headers: { ...standaloneHeaders(), ...(options?.headers ?? {}) },
    });
    if (!res.ok) {
        let err: { error?: string } = { error: `HTTP ${res.status}` };
        try { err = await res.json() as { error?: string }; } catch { /* ignore */ }
        throw new Error(err.error ?? `HTTP ${res.status}`);
    }
    return res.json() as Promise<T>;
}

export const standaloneApi = {
    // ── Auth ──────────────────────────────────────────────────────────────
    register: (body: { email: string; password: string; firstName: string; lastName: string; company?: string }) =>
        standaloneFetch<{ token: string; user: StandaloneUser }>('/standalone/auth/register', {
            method: 'POST', body: JSON.stringify(body),
        }),

    login: (body: { email: string; password: string }) =>
        standaloneFetch<{ token: string; user: StandaloneUser }>('/standalone/auth/login', {
            method: 'POST', body: JSON.stringify(body),
        }),

    getMe: () =>
        standaloneFetch<{ user: StandaloneUser }>('/standalone/auth/me'),

    forgotPassword: (email: string) =>
        standaloneFetch<{ success: boolean; message: string; resetToken?: string }>('/standalone/auth/forgot-password', {
            method: 'POST', body: JSON.stringify({ email }),
        }),

    resetPassword: (token: string, password: string) =>
        standaloneFetch<{ success: boolean; token: string }>('/standalone/auth/reset-password', {
            method: 'POST', body: JSON.stringify({ token, password }),
        }),

    resendVerification: () =>
        standaloneFetch<{ success: boolean; message: string }>('/standalone/auth/resend-verification', {
            method: 'POST',
        }),

    // ── Contacts ──────────────────────────────────────────────────────────
    getContacts: () =>
        standaloneFetch<{ contacts: AppContact[] }>('/app/contacts'),

    getContact: (id: string) =>
        standaloneFetch<{ contact: AppContact }>(`/app/contacts/${id}`),

    createContact: (body: { name: string; email?: string; phone?: string; company?: string; address?: string }) =>
        standaloneFetch<{ contact: AppContact }>('/app/contacts', {
            method: 'POST', body: JSON.stringify(body),
        }),

    updateContact: (id: string, body: Partial<AppContact>) =>
        standaloneFetch<{ contact: AppContact }>(`/app/contacts/${id}`, {
            method: 'PUT', body: JSON.stringify(body),
        }),

    deleteContact: (id: string) =>
        standaloneFetch<{ success: boolean }>(`/app/contacts/${id}`, { method: 'DELETE' }),

    importCSV: async (file: File): Promise<{ imported: number; contacts: AppContact[] }> => {
        const form = new FormData();
        form.append('file', file);
        const res = await fetch(`${API_BASE}/app/contacts/import-csv`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${getStandaloneToken()}` },
            body: form,
        });
        if (!res.ok) { const e = await res.json() as { error?: string }; throw new Error(e.error ?? 'Import failed'); }
        return res.json() as Promise<{ imported: number; contacts: AppContact[] }>;
    },

    // ── App API (mirrors GHL api shape, hits /api/app/* routes) ──────────
    uploadFiles: async (files: File[]): Promise<{ files: Array<{ id: string; name: string; size: number; status: string }> }> => {
        const form = new FormData();
        files.forEach(f => form.append('files', f));
        const res = await fetch(`${API_BASE}/app/uploads`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${getStandaloneToken()}` },
            body: form,
        });
        if (!res.ok) { const e = await res.json() as { error?: string }; throw new Error(e.error ?? 'Upload failed'); }
        return res.json() as Promise<{ files: Array<{ id: string; name: string; size: number; status: string }> }>;
    },

    extractFields: (body: { fileIds: string[]; propertyType: string }) =>
        standaloneFetch<RawExtractionSnapshot>('/app/extract', {
            method: 'POST', body: JSON.stringify(body),
        }),

    startAnalysis: (body: AnalysisStartBody) =>
        standaloneFetch<{ jobId: string }>('/app/analysis/start', {
            method: 'POST', body: JSON.stringify(body),
        }),

    pollAnalysis: (jobId: string) =>
        standaloneFetch<{ status: string; progress: number; stage: string; reportId?: string; error?: string }>(`/app/analysis/${jobId}`),

    getReport: (reportId: string) =>
        standaloneFetch<{ report: Report }>(`/app/reports/${reportId}`),

    getAnalysisTrace: (reportId: string) =>
        standaloneFetch<{ trace: AnalysisTrace }>(`/app/reports/${reportId}/trace`),

    getReports: () =>
        standaloneFetch<{ reports: Report[] }>('/app/reports'),

    downloadReport: async (reportId: string): Promise<Blob> => {
        const res = await fetch(`${API_BASE}/app/reports/${reportId}/download`, {
            headers: { 'Authorization': `Bearer ${getStandaloneToken()}` },
        });
        if (!res.ok) throw new Error('Download failed');
        return res.blob();
    },

    deleteReport: (reportId: string) =>
        standaloneFetch<{ success: boolean }>(`/app/reports/${reportId}`, { method: 'DELETE' }),

    archiveReport: (reportId: string, archived: boolean) =>
        standaloneFetch<{ success: boolean }>(`/app/reports/${reportId}/archive`, {
            method: 'PATCH', body: JSON.stringify({ archived }),
        }),

    updateReportSection: (reportId: string, sectionKey: string, sectionData: Record<string, unknown>) =>
        standaloneFetch<{ success: boolean }>(`/app/reports/${reportId}/sections/${sectionKey}`, {
            method: 'PUT', body: JSON.stringify(sectionData),
        }),

    getTemplates: () =>
        standaloneFetch<{ templates: LOITemplate[] }>('/app/loi/templates'),

    createTemplate: (body: { name: string; propertyType: string; htmlContent: string; emailJson?: unknown }) =>
        standaloneFetch<LOITemplate>('/app/loi/templates', { method: 'POST', body: JSON.stringify(body) }),

    updateTemplate: (id: string, body: { name?: string; htmlContent?: string; emailJson?: unknown }) =>
        standaloneFetch<LOITemplate>(`/app/loi/templates/${id}`, { method: 'PUT', body: JSON.stringify(body) }),

    deleteTemplate: (id: string) =>
        standaloneFetch<{ success: boolean }>(`/app/loi/templates/${id}`, { method: 'DELETE' }),

    previewTemplate: async (id: string): Promise<Blob> => {
        const res = await fetch(`${API_BASE}/app/loi/templates/${id}/preview`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${getStandaloneToken()}` },
        });
        if (!res.ok) {
            let errorPayload: { error?: string } = { error: 'Preview failed' };
            try { errorPayload = await res.json() as { error?: string }; } catch { /* ignore */ }
            throw new Error(errorPayload.error ?? 'Preview failed');
        }
        return res.blob();
    },

    generateLOI: (body: {
        reportId: string; templateId: string; tier: string; contactId?: string;
        mode?: 'ai' | 'template';
        options: { contingencies: boolean; earnest: boolean; timeline: boolean; contingenciesText?: string; earnestText?: string; timelineText?: string };
    }) =>
        standaloneFetch<{ loiId: string; downloadUrl: string }>('/app/loi/generate', {
            method: 'POST', body: JSON.stringify(body),
        }),

    getLOI: (loiId: string) =>
        standaloneFetch<LOI>(`/app/loi/${loiId}`),

    updateLOIContent: (loiId: string, body: { content: string; htmlContent?: string }) =>
        standaloneFetch<{ success: boolean; downloadUrl: string }>(`/app/loi/${loiId}/content`, {
            method: 'PATCH', body: JSON.stringify(body),
        }),

    getLOIs: () =>
        standaloneFetch<{ lois: LOI[] }>('/app/lois'),

    getSettings: () =>
        standaloneFetch<{ settings: InvestorSettings }>('/app/settings'),

    updateSettings: (settings: Partial<InvestorSettings>) =>
        standaloneFetch<{ settings: InvestorSettings; success: boolean }>('/app/settings', {
            method: 'PUT', body: JSON.stringify(settings),
        }),
};

interface InvestorSettings {
    locationId: string;
    holdPeriod: number;
    tier3Irr: number;
    tier2Irr: number;
    tier1Irr: number;
    tier3Label: string;
    tier2Label: string;
    tier1Label: string;
    tier3Assumption: 'conservative' | 'realistic';
    tier2Assumption: 'conservative' | 'realistic';
    tier1Assumption: 'conservative' | 'realistic';
    targetCoc: number | null;
    targetEquityMultiple: number | null;
    targetDscr: number;
    preferredFinancing: 'agency_perm' | 'bridge' | 'best_fit';
    exitStrategy: 'sale' | 'refinance' | 'hold';
    customNotes: string;
    autoSaveReport?: boolean;
    loiContingenciesText?: string;
    loiEarnestText?: string;
    loiTimelineText?: string;
    loiDisclaimer?: string;
    reportDisclaimer?: string;
}

interface LOITemplate {
    id: string;
    name: string;
    propertyType: 'single' | 'multi';
    htmlContent: string;
    emailJson?: unknown;
    isSystem: boolean;
    createdAt: string;
    updatedAt: string;
}

interface GHLCustomField {
    id: string;
    name: string;
    fieldKey: string;
    dataType: 'TEXT' | 'LARGE_TEXT' | 'NUMERICAL' | 'PHONE' | 'EMAIL' | 'DATE' | 'CHECKBOX' | 'DROPDOWN' | 'URL' | 'MONETARY';
    position?: number;
    placeholder?: string;
}

export type { Contact, Report, AnalysisTrace, LOI, InvestorSettings, LOITemplate, GHLCustomField, StandaloneUser, StandaloneContact, AppContact, RawExtractionSnapshot, AnalysisStartBody };
